import PropTypes from "prop-types";
import React, { useEffect, Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./helpers/scroll-top";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { multilanguage, loadLanguages } from "redux-multilanguage";
import { connect } from "react-redux";
import { composeLink, getStaticPageLink } from "./utils/links";
import englishTranslations from "./translations/english.json";
import frenchTranslations from "./translations/french.json";
import germanTranslations from "./translations/germany.json";

import BaseLayout from "./layouts/magnolia/BaseLayout";
import PageLoader from "./PageLoader";
import Cart from "./pages/magnolia/cart/Cart";
import Checkout from "./pages/magnolia/checkout/Checkout";
import Compare from "./pages/magnolia/compare/Compare";
import Wishlist from "./pages/magnolia/wishlist/Wishlist";
import LoginRegister from "./pages/magnolia/account/LoginRegister";
import MyAccount from "./pages/magnolia/account/MyAccount";

// home pages
const HomeFashion = lazy(() => import("./pages/home/HomeFashion"));
const HomeFashionTwo = lazy(() => import("./pages/home/HomeFashionTwo"));
const HomeFashionThree = lazy(() => import("./pages/home/HomeFashionThree"));
const HomeFashionFour = lazy(() => import("./pages/home/HomeFashionFour"));
const HomeFashionFive = lazy(() => import("./pages/home/HomeFashionFive"));
const HomeFashionSix = lazy(() => import("./pages/home/HomeFashionSix"));
const HomeFashionSeven = lazy(() => import("./pages/home/HomeFashionSeven"));
const HomeKidsFashion = lazy(() => import("./pages/home/HomeKidsFashion"));
const HomeCosmetics = lazy(() => import("./pages/home/HomeCosmetics"));
const HomeFurniture = lazy(() => import("./pages/home/HomeFurniture"));
const HomeFurnitureTwo = lazy(() => import("./pages/home/HomeFurnitureTwo"));
const HomeFurnitureThree = lazy(() => import("./pages/home/HomeFurnitureThree"));
const HomeElectronics = lazy(() => import("./pages/home/HomeElectronics"));
const HomeElectronicsTwo = lazy(() => import("./pages/home/HomeElectronicsTwo"));
const HomeBookStore = lazy(() => import("./pages/home/HomeBookStore"));
const HomePlants = lazy(() => import("./pages/home/HomePlants"));
const HomeFlowerShop = lazy(() => import("./pages/home/HomeFlowerShop"));
const HomeOrganicFood = lazy(() => import("./pages/home/HomeOrganicFood"));
const HomeOrganicFoodTwo = lazy(() => import("./pages/home/HomeOrganicFoodTwo"));
const HomeGridBanner = lazy(() => import("./pages/home/HomeGridBanner"));
const HomeAutoParts = lazy(() => import("./pages/home/HomeAutoParts"));
const HomeCakeShop = lazy(() => import("./pages/home/HomeCakeShop"));
const HomeHandmade = lazy(() => import("./pages/home/HomeHandmade"));
const HomePetFood = lazy(() => import("./pages/home/HomePetFood"));

// shop pages
const ShopGridStandard = lazy(() => import("./pages/shop/ShopGridStandard"));
const ShopGridFilter = lazy(() => import("./pages/shop/ShopGridFilter"));
const ShopGridTwoColumn = lazy(() => import("./pages/shop/ShopGridTwoColumn"));
const ShopGridNoSidebar = lazy(() => import("./pages/shop/ShopGridNoSidebar"));
const ShopGridFullWidth = lazy(() => import("./pages/shop/ShopGridFullWidth"));
const ShopGridRightSidebar = lazy(() => import("./pages/shop/ShopGridRightSidebar"));
const ShopListStandard = lazy(() => import("./pages/shop/ShopListStandard"));
const ShopListFullWidth = lazy(() => import("./pages/shop/ShopListFullWidth"));
const ShopListTwoColumn = lazy(() => import("./pages/shop/ShopListTwoColumn"));

// product pages
const Product = lazy(() => import("./pages/shop-product/Product"));
const ProductTabLeft = lazy(() => import("./pages/shop-product/ProductTabLeft"));
const ProductTabRight = lazy(() => import("./pages/shop-product/ProductTabRight"));
const ProductSticky = lazy(() => import("./pages/shop-product/ProductSticky"));
const ProductSlider = lazy(() => import("./pages/shop-product/ProductSlider"));
const ProductFixedImage = lazy(() => import("./pages/shop-product/ProductFixedImage"));

// blog pages
const BlogStandard = lazy(() => import("./pages/blog/BlogStandard"));
const BlogNoSidebar = lazy(() => import("./pages/blog/BlogNoSidebar"));
const BlogRightSidebar = lazy(() => import("./pages/blog/BlogRightSidebar"));
const BlogDetailsStandard = lazy(() => import("./pages/blog/BlogDetailsStandard"));

// other pages
const About = lazy(() => import("./pages/other/About"));
const Contact = lazy(() => import("./pages/other/Contact"));

// const Cart = lazy(() => import("./pages/other/Cart"));
// const Checkout = lazy(() => import("./pages/other/Checkout"));

const NotFound = lazy(() => import("./pages/other/NotFound"));

const App = ({ currentLanguageCode, dispatch }) => {
    useEffect(() => {
        dispatch(
            loadLanguages({
                languages: {
                    en: englishTranslations,
                    fr: frenchTranslations,
                    de: germanTranslations,
                },
            }),
        );
    });

    return (
        <HelmetProvider>
            <Router>
                <BaseLayout>
                    <ScrollToTop>
                        <Toaster position="bottom-left" />
                        <Suspense
                            fallback={
                                <div className="flone-preloader-wrapper">
                                    <div className="flone-preloader">
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            }
                        >
                            <Switch>
                                {/* Homepages */}
                                <Route path={process.env.PUBLIC_URL + "/home-fashion"} component={HomeFashion} />
                                <Route path={process.env.PUBLIC_URL + "/home-fashion-two"} component={HomeFashionTwo} />
                                <Route path={process.env.PUBLIC_URL + "/home-fashion-three"} component={HomeFashionThree} />
                                <Route path={process.env.PUBLIC_URL + "/home-fashion-four"} component={HomeFashionFour} />
                                <Route path={process.env.PUBLIC_URL + "/home-fashion-five"} component={HomeFashionFive} />
                                <Route path={process.env.PUBLIC_URL + "/home-fashion-six"} component={HomeFashionSix} />
                                <Route path={process.env.PUBLIC_URL + "/home-fashion-seven"} component={HomeFashionSeven} />
                                <Route path={process.env.PUBLIC_URL + "/home-kids-fashion"} component={HomeKidsFashion} />
                                <Route path={process.env.PUBLIC_URL + "/home-cosmetics"} component={HomeCosmetics} />
                                <Route path={process.env.PUBLIC_URL + "/home-furniture"} component={HomeFurniture} />
                                <Route path={process.env.PUBLIC_URL + "/home-furniture-two"} component={HomeFurnitureTwo} />
                                <Route path={process.env.PUBLIC_URL + "/home-furniture-three"} component={HomeFurnitureThree} />
                                <Route path={process.env.PUBLIC_URL + "/home-electronics"} component={HomeElectronics} />
                                <Route path={process.env.PUBLIC_URL + "/home-electronics-two"} component={HomeElectronicsTwo} />
                                <Route path={process.env.PUBLIC_URL + "/home-book-store"} component={HomeBookStore} />
                                <Route path={process.env.PUBLIC_URL + "/home-plants"} component={HomePlants} />
                                <Route path={process.env.PUBLIC_URL + "/home-flower-shop"} component={HomeFlowerShop} />
                                <Route path={process.env.PUBLIC_URL + "/home-organic-food"} component={HomeOrganicFood} />
                                <Route path={process.env.PUBLIC_URL + "/home-organic-food-two"} component={HomeOrganicFoodTwo} />
                                <Route path={process.env.PUBLIC_URL + "/home-grid-banner"} component={HomeGridBanner} />
                                <Route path={process.env.PUBLIC_URL + "/home-auto-parts"} component={HomeAutoParts} />
                                <Route path={process.env.PUBLIC_URL + "/home-cake-shop"} component={HomeCakeShop} />
                                <Route path={process.env.PUBLIC_URL + "/home-handmade"} component={HomeHandmade} />
                                <Route path={process.env.PUBLIC_URL + "/home-pet-food"} component={HomePetFood} />

                                {/* Shop pages */}
                                <Route path={process.env.PUBLIC_URL + "/shop-grid-standard"} component={ShopGridStandard} />
                                <Route path={process.env.PUBLIC_URL + "/shop-grid-filter"} component={ShopGridFilter} />
                                <Route path={process.env.PUBLIC_URL + "/shop-grid-two-column"} component={ShopGridTwoColumn} />
                                <Route path={process.env.PUBLIC_URL + "/shop-grid-no-sidebar"} component={ShopGridNoSidebar} />
                                <Route path={process.env.PUBLIC_URL + "/shop-grid-full-width"} component={ShopGridFullWidth} />
                                <Route path={process.env.PUBLIC_URL + "/shop-grid-right-sidebar"} component={ShopGridRightSidebar} />
                                <Route path={process.env.PUBLIC_URL + "/shop-list-standard"} component={ShopListStandard} />
                                <Route path={process.env.PUBLIC_URL + "/shop-list-full-width"} component={ShopListFullWidth} />
                                <Route path={process.env.PUBLIC_URL + "/shop-list-two-column"} component={ShopListTwoColumn} />

                                {/* Shop product pages */}
                                <Route
                                    path={process.env.PUBLIC_URL + "/product/:id"}
                                    render={(routeProps) => <Product {...routeProps} key={routeProps.match.params.id} />}
                                />
                                <Route path={process.env.PUBLIC_URL + "/product-tab-left/:id"} component={ProductTabLeft} />
                                <Route path={process.env.PUBLIC_URL + "/product-tab-right/:id"} component={ProductTabRight} />
                                <Route path={process.env.PUBLIC_URL + "/product-sticky/:id"} component={ProductSticky} />
                                <Route path={process.env.PUBLIC_URL + "/product-slider/:id"} component={ProductSlider} />
                                <Route path={process.env.PUBLIC_URL + "/product-fixed-image/:id"} component={ProductFixedImage} />

                                {/* Blog pages */}
                                <Route path={process.env.PUBLIC_URL + "/blog-standard"} component={BlogStandard} />
                                <Route path={process.env.PUBLIC_URL + "/blog-no-sidebar"} component={BlogNoSidebar} />
                                <Route path={process.env.PUBLIC_URL + "/blog-right-sidebar"} component={BlogRightSidebar} />
                                <Route path={process.env.PUBLIC_URL + "/blog-details-standard"} component={BlogDetailsStandard} />

                                {/* Other pages */}
                                <Route path={process.env.PUBLIC_URL + "/about"} component={About} />
                                <Route path={process.env.PUBLIC_URL + "/contact"} component={Contact} />

                                {/* <Route path={process.env.PUBLIC_URL + "/cart"} component={Cart} /> */}
                                {/* <Route path={process.env.PUBLIC_URL + "/checkout"} component={Checkout} /> */}

                                <Route path={process.env.PUBLIC_URL + "/not-found"} component={NotFound} />

                                <Route path={composeLink(getStaticPageLink("CART"), currentLanguageCode)}>
                                    <Cart />
                                </Route>

                                <Route path={composeLink(getStaticPageLink("CHECKOUT"), currentLanguageCode)}>
                                    <Checkout />
                                </Route>

                                <Route path={composeLink(getStaticPageLink("COMPARE"), currentLanguageCode)}>
                                    <Compare />
                                </Route>

                                <Route path={composeLink(getStaticPageLink("WISHLIST"), currentLanguageCode)}>
                                    <Wishlist />
                                </Route>

                                <Route path={composeLink(getStaticPageLink("LOGIN_REGISTER"), currentLanguageCode)}>
                                    <LoginRegister />
                                </Route>

                                <Route path={composeLink(getStaticPageLink("MY_ACCOUNT"), currentLanguageCode)}>
                                    <MyAccount />
                                </Route>

                                <Route path="*">
                                    <PageLoader />
                                </Route>
                            </Switch>
                        </Suspense>
                    </ScrollToTop>
                </BaseLayout>
            </Router>
        </HelmetProvider>
    );
};

App.propTypes = {
    dispatch: PropTypes.func,
};

export default connect()(multilanguage(App));
